import { CalendarDays, Menu, Plus, Search } from 'lucide-react'
import { useEffect, useState } from 'react'
import { journalId } from '@solar/core'
import { handleSelectKey, isMac } from '../client/keys.ts'
import type { User } from '../client/api.ts'
import { ghostForDay } from '../client/store.ts'
import { CommandBar } from './CommandBar.tsx'
import { todayKey } from './dates.ts'
import { useStore, useSyncStatus } from './hooks.ts'
import { HoverPreview } from './HoverPreview.tsx'
import { Journal } from './Journal.tsx'
import { NodeView } from './NodeView.tsx'
import { Picker } from './Picker.tsx'
import { navigate, useRoute } from './router.ts'
import { Settings } from './Settings.tsx'
import { LeftSidebar, RightSidebar } from './Sidebars.tsx'

function SyncDot() {
  const s = useSyncStatus()
  const cls = s.state === 'offline' ? 'off' : s.pending > 0 ? 'pending' : s.state === 'online' ? 'ok' : 'starting'
  const title = s.state === 'offline' ? `Offline${s.pending ? `, ${s.pending} change(s) waiting` : ''}` : s.pending ? `${s.pending} change(s) syncing` : 'Synced'
  return <span className={`syncdot ${cls}`} title={title} aria-label={title} data-state={cls} />
}

export function App({ user, onLogout }: { user: User; onLogout: () => void }) {
  const store = useStore()
  const route = useRoute()
  const [cmd, setCmd] = useState(false)
  const [menu, setMenu] = useState(false)

  // Recently visited (the command bar shows them before you type).
  useEffect(() => {
    if (route.name === 'node') store.visit({ kind: 'node', id: route.id })
    else if (route.name === 'journal' && route.date) store.visit({ kind: 'day', date: route.date })
  }, [store, route.name === 'node' ? route.id : route.name === 'journal' ? route.date : null]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.isComposing) return
      const mod = isMac ? e.metaKey : e.ctrlKey
      const k = e.key.toLowerCase()
      if (mod && !e.altKey && (k === 'k' || e.code === 'KeyK')) {
        e.preventDefault()
        if (e.shiftKey) {
          // ⌘⇧K: the current node (or day) in the right sidebar.
          const id = route.name === 'node' ? route.id : journalId(route.name === 'journal' && route.date ? route.date : todayKey())
          store.openInSidebar({ kind: 'node', id })
        } else {
          store.commitDraft(true)
          setCmd((open) => !open)
        }
        return
      }
      if (mod && !e.altKey && e.code === 'Backslash') {
        e.preventDefault()
        if (e.shiftKey) store.setSidebar({ open: !store.sidebar.open })
        else store.setSidebar({ left: !store.sidebar.left })
        return
      }
      if (mod && e.shiftKey && k === 't') {
        e.preventDefault()
        navigate('/')
        return
      }
      if (e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLInputElement || e.target instanceof HTMLSelectElement) return
      if (cmd) return
      handleSelectKey(store, e)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [store, route, cmd])

  /** Phone `+` (SPEC §3.3: jot = 1 action): the caret on the line after today's last block. */
  const jot = (): void => {
    const date = todayKey()
    if (route.name !== 'journal' || route.date) navigate('/')
    store.edit(ghostForDay(date), journalId(date))
  }

  const closeCmd = (): void => {
    setCmd(false)
    // Back to where you were typing, if you were.
    const c = store.currentCaret()
    if (c) store.setCaret(c)
  }

  return (
    <div className="shell">
      <LeftSidebar
        footer={
          <div className="me">
            <SyncDot />
            <a className="who" href="/settings" title="Settings" onClick={(e) => (e.preventDefault(), navigate('/settings'))}>
              {user.username}
            </a>
            <button className="link" onClick={onLogout}>
              Sign out
            </button>
          </div>
        }
      />
      <main className="main">
        {route.name === 'journal' && <Journal key="journal" date={route.date} block={route.block} />}
        {route.name === 'node' && <NodeView key={route.id} id={route.id} block={route.block} />}
        {route.name === 'settings' && <Settings />}
      </main>
      <RightSidebar />
      <nav className="bottombar" aria-label="Phone menu">
        <button aria-label="Journal" onClick={() => navigate('/')}>
          <CalendarDays size={22} />
        </button>
        <button aria-label="Search" onClick={() => setCmd(true)}>
          <Search size={22} />
        </button>
        <button aria-label="New block" className="jot" onClick={() => jot()}>
          <Plus size={26} />
        </button>
        <button aria-label="Menu" onClick={() => setMenu(true)}>
          <Menu size={22} />
        </button>
      </nav>
      {menu && (
        <div className="sheet" role="dialog" aria-label="Menu" onClick={() => setMenu(false)}>
          <div className="sheet-body" onClick={(e) => e.stopPropagation()}>
            <LeftSidebar
              force
              footer={
                <div className="me">
                  <SyncDot />
                  <a className="who" href="/settings" onClick={(e) => (e.preventDefault(), setMenu(false), navigate('/settings'))}>
                    Settings
                  </a>
                  <button className="link" onClick={onLogout}>
                    Sign out
                  </button>
                </div>
              }
              onNavigate={() => setMenu(false)}
            />
          </div>
        </div>
      )}
      <Picker />
      <HoverPreview />
      {cmd && <CommandBar onClose={closeCmd} />}
    </div>
  )
}
